import {
  CreateAdGroupPayload,
  CreateCampaignBudgetPayload,
  CreateCampaignPayload
} from 'shared/src/google-ads/google-ads-types';
import type {
  AdapterCapabilities,
  AdapterConfig,
  AdapterSchemas,
  AncestryMap
} from '../skill-platform-types';

export const googleAdsAdapterConfig: AdapterConfig = {
  platformName: 'Google Ads',
  platformShortLabel: 'GA',
  fullHierarchy: [
    { key: 'account', label: 'Customer (Account)' },
    { key: 'campaign_budget', label: 'Campaign Budget' },
    { key: 'campaign', label: 'Campaign' },
    { key: 'ad_group', label: 'Ad Group' }
  ],
  createSequence: ['campaign_budget', 'campaign', 'ad_group'],
  requiredAncestors: ['account'],
  rootEntityType: 'account',
  mediaBuyEntityType: 'ad_group',
  workflowSteps: ['Configure', 'Review']
};

export function buildGoogleAdsEntityUrl(
  entityType: string,
  entityId: string,
  ancestry?: AncestryMap
): string {
  const accountId = ancestry?.get('account')?.platformEntityId;
  const accountQs = accountId ? `?ocid=${accountId}` : '';
  if (entityType === 'campaign') {
    return `https://ads.google.com/aw/campaigns${accountQs}${accountQs ? '&' : '?'}campaignId=${entityId}`;
  }
  if (entityType === 'ad_group') {
    return `https://ads.google.com/aw/adgroups${accountQs}${accountQs ? '&' : '?'}adGroupId=${entityId}`;
  }
  if (entityType === 'campaign_budget') {
    return `https://ads.google.com/aw/campaigns/sharedlibrary/budgets${accountQs}`;
  }
  if (entityType === 'account') {
    return `https://ads.google.com/aw/overview${accountQs}`;
  }
  return `https://ads.google.com/`;
}

export const googleAdsAdapterSchemas: AdapterSchemas = {
  entityPayloads: {
    campaign_budget: CreateCampaignBudgetPayload,
    campaign: CreateCampaignPayload,
    ad_group: CreateAdGroupPayload
  }
};

export const googleAdsCapabilities: AdapterCapabilities = {
  lineItemTypes: ['search'],
  lineItemTypeAliases: {
    search: ['paid search', 'sem', 'google search', 'ppc']
  },
  requiresAccountId: true,
  biddingStrategies: [
    'MANUAL_CPC',
    'MAXIMIZE_CONVERSIONS',
    'TARGET_CPA',
    'TARGET_ROAS',
    'MAXIMIZE_CLICKS'
  ],
  // New SEM campaigns always start on MAXIMIZE_CLICKS (trader spec); the card pre-selects it.
  defaultBiddingStrategy: 'MAXIMIZE_CLICKS',
  // No pacingOptions: Google Ads has no pacing concept, so the card hides its pacing dropdown.
  budgetTypes: ['daily'],
  // Google Ads can split a line item into multiple campaigns (brand vs non-brand, per-geo), each its
  // own media plan, with budget shares summing to the line-item price (converted to media budget
  // server-side). Default stays one campaign.
  supportsCampaignSplit: true,
  targetingModules: [
    {
      key: 'geo.location',
      label: 'Geo Target',
      kind: 'multi-select',
      optionsSource: 'zoho-derived',
      zohoField: 'Geo_Targets'
    },
    {
      key: 'language',
      label: 'Language',
      kind: 'multi-select',
      optionsSource: 'static',
      staticOptions: [
        { id: '1000', label: 'English' },
        { id: '1003', label: 'Spanish' },
        { id: '1014', label: 'French' }
      ]
    },
    {
      key: 'device.type',
      label: 'Device',
      kind: 'multi-select',
      optionsSource: 'static',
      staticOptions: [
        { id: 'MOBILE', label: 'Mobile' },
        { id: 'DESKTOP', label: 'Desktop' },
        { id: 'TABLET', label: 'Tablet' }
      ]
    }
  ]
};
